import { useEffect, useState } from "react";

import { Card, Icon, Notification } from "@stellar/design-system";

import { ErrorWithExtras } from "@/components/ErrorWithExtras";
import { LoadingContent } from "@/components/LoadingContent";
import { NewUserModal } from "@/components/NewUserModal";
import { NotificationWithButtons } from "@/components/NotificationWithButtons";

import { CO_SIGNING_MAX_SIGNERS } from "@/constants/settings";

import { useCoSigningStatus } from "@/apiQueries/multisigEnrollment";
import { useCreateNewUser } from "@/apiQueries/useCreateNewUser";
import { useUpdateUserRole } from "@/apiQueries/useUpdateUserRole";
import { useUpdateUserStatus } from "@/apiQueries/useUpdateUserStatus";
import { useUsers } from "@/apiQueries/useUsers";

import { userRoleText } from "@/helpers/userRoleText";

import { useCoSigningPause } from "@/hooks/useCoSigningPause";
import { useRedux } from "@/hooks/useRedux";

import { AboutRolesModal } from "./components/AboutRolesModal";
import { ChangeRoleModal } from "./components/ChangeRoleModal";
import { CoSignerSigningModal, CoSignerSigningTarget } from "./components/CoSignerSigningModal";
import { MembersTable } from "./components/MembersTable";
import { ToggleStatusModal } from "./components/ToggleStatusModal";
import { signerManagementStubs } from "./signerManagementStubs";
import { getCoSignerRowInfo } from "./utils/coSignerLookup";

import "./styles.scss";

import { ApiUser, NewUser, UserRole } from "@/types";

const getUserNameText = (firstName?: string, lastName?: string) => {
  const parts = [firstName, lastName].filter((p): p is string => Boolean(p));
  return parts.length ? parts.join(" ") : "this user";
};

export const SettingsTeamMembers = () => {
  const [selectedUser, setSelectedUser] = useState<ApiUser | null>(null);
  const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
  const [isRoleModalVisible, setIsRoleModalVisible] = useState(false);
  const [isNewUserModalVisible, setIsNewUserModalVisible] = useState(false);
  const [isAboutRolesOpen, setIsAboutRolesOpen] = useState(false);
  const [updatedUser, setUpdatedUser] = useState<{
    id: string;
    role?: UserRole | null;
    isActive?: boolean;
    /**
     * Whether the user was an approved co-signer at the moment of the
     * status change. Drives the deactivate banner body variant — approved
     * co-signers get the "removing them as a co-signer is a separate step
     * that needs Owner approval" reminder; everyone else gets the shorter
     * "you can reactivate them at any time" copy.
     */
    wasApprovedCoSigner?: boolean;
  } | null>(null);

  const { profile } = useRedux("profile");
  const currentUserId = profile.data.id;

  const { data: coSigningStatus } = useCoSigningStatus({
    userId: currentUserId,
    role: profile.data.role ?? null,
  });
  const {
    simulateInvitedToPending,
    approvePendingSigner,
    rejectPendingSigner,
    removeCoSigner,
    inviteCoSigner,
    revokeCoSignerInvitation,
  } = signerManagementStubs;

  const isOwnerViewer = coSigningStatus?.me.role === "owner";
  // Inviting a user doesn't touch co-signer state, so it shouldn't depend on
  // `coSigningStatus` resolving — that composite stays `undefined` (hiding
  // the button with no error) while any of its underlying queries is still
  // loading or failed, even though the Owner role itself is already known.
  const isOwner = profile.data.role === "owner";
  const { isPaused: isCoSigningPaused } = useCoSigningPause();

  const [signingIntent, setSigningIntent] = useState<"review" | "remove" | null>(null);
  const [signingTarget, setSigningTarget] = useState<CoSignerSigningTarget | null>(null);
  const isSigningModalVisible = signingIntent !== null && signingTarget !== null;

  const closeSigningModal = () => {
    setSigningIntent(null);
    setSigningTarget(null);
  };

  const handleReviewCoSignerRequest = (user: ApiUser) => {
    if (!isOwnerViewer) return;
    const pending = coSigningStatus?.org.pending_signer;
    if (!pending || pending.user_id !== user.id) return;
    setSigningIntent("review");
    setSigningTarget({
      user_id: pending.user_id,
      name: `${pending.first_name} ${pending.last_name}`.trim() || user.email,
      email: user.email,
      publicKey: pending.stellar_public_key,
      role: pending.role,
    });
  };

  const handleRemoveCoSignerRequest = (user: ApiUser) => {
    if (!isOwnerViewer) return;
    const active = coSigningStatus?.org.active_signers.find((s) => s.user_id === user.id);
    if (!active) return;
    setSigningIntent("remove");
    setSigningTarget({
      user_id: active.user_id,
      name: `${active.first_name} ${active.last_name}`.trim() || user.email,
      email: user.email,
      publicKey: active.stellar_public_key,
      role: active.role,
    });
  };

  const handleInviteCoSignerRequest = (user: ApiUser) => {
    if (!isOwnerViewer) return;
    const primaryRole = user.roles?.[0];
    const signerRole: "OWNER" | "FINANCIAL_CONTROLLER" | null =
      primaryRole === "owner"
        ? "OWNER"
        : primaryRole === "financial_controller"
          ? "FINANCIAL_CONTROLLER"
          : null;
    if (!signerRole) return;
    inviteCoSigner({
      user_id: user.id,
      first_name: user.first_name ?? "",
      last_name: user.last_name ?? "",
      role: signerRole,
    });
  };

  const handleRevokeInvitationRequest = (user: ApiUser) => {
    if (!isOwnerViewer) return;
    revokeCoSignerInvitation(user.id);
  };

  const {
    data: usersData,
    error: usersError,
    isLoading: isUsersLoading,
    isFetching: isUsersFetching,
    refetch: getUsers,
  } = useUsers();

  const {
    error: roleError,
    isPending: isRolePending,
    isSuccess: isRoleSuccess,
    isError: isRoleError,
    mutateAsync: updateRole,
    reset: resetRole,
  } = useUpdateUserRole();

  const {
    error: statusError,
    isPending: isStatusPending,
    isSuccess: isStatusSuccess,
    isError: isStatusError,
    mutateAsync: updateStatus,
    reset: resetStatus,
  } = useUpdateUserStatus();

  const {
    data: newUser,
    error: newUserError,
    isPending: isNewUserPending,
    isSuccess: isNewUserSuccess,
    isError: isNewUserError,
    mutateAsync: createNewUser,
    reset: resetNewUser,
  } = useCreateNewUser();

  const hideModal = () => {
    setIsStatusModalVisible(false);
    setIsRoleModalVisible(false);
    setIsNewUserModalVisible(false);
    setSelectedUser(null);
  };

  useEffect(() => {
    if (isRoleSuccess || isStatusSuccess) {
      getUsers();
      hideModal();
    }

    if (isRoleError || isStatusError) {
      hideModal();
    }

    return () => {
      if (isRoleSuccess || isRoleError) {
        resetRole();
      }

      if (isStatusSuccess || isStatusError) {
        resetStatus();
      }
    };
  }, [
    getUsers,
    isRoleError,
    isRoleSuccess,
    isStatusError,
    isStatusSuccess,
    resetRole,
    resetStatus,
  ]);

  useEffect(() => {
    if (isNewUserSuccess) {
      getUsers();
      hideModal();
    }

    return () => {
      if (isNewUserSuccess) {
        resetNewUser();
      }
    };
  }, [getUsers, isNewUserSuccess, resetNewUser]);

  const handleChangeRoleRequest = (user: ApiUser) => {
    if (!isOwnerViewer) return;
    resetRole();
    resetStatus();
    setSelectedUser(user);
    setIsRoleModalVisible(true);
  };

  const handleToggleStatusRequest = (user: ApiUser) => {
    if (!isOwnerViewer) return;
    resetRole();
    resetStatus();
    setSelectedUser(user);
    setIsStatusModalVisible(true);
  };

  /**
   * Reactivate is immediate — matches the standalone prototype where the
   * kebab item flips `is_active` without opening the ToggleStatusModal.
   */
  const handleReactivateRequest = (user: ApiUser) => {
    if (!isOwnerViewer) return;
    resetRole();
    resetStatus();
    updateStatus({ userId: user.id, isActive: true });
    setUpdatedUser({ id: user.id, isActive: true });
  };

  const handleConfirmRole = (nextRole: UserRole) => {
    if (selectedUser?.id && nextRole) {
      updateRole({ userId: selectedUser.id, role: nextRole });
      setUpdatedUser({ id: selectedUser.id, role: nextRole });
    }
  };

  const handleConfirmStatus = () => {
    if (selectedUser?.id) {
      const nextActive = !selectedUser.is_active;
      const wasApprovedCoSigner =
        !nextActive && getCoSignerRowInfo(selectedUser.id, coSigningStatus)?.state === "approved";
      updateStatus({ userId: selectedUser.id, isActive: nextActive });
      setUpdatedUser({ id: selectedUser.id, isActive: nextActive, wasApprovedCoSigner });
    }
  };

  const handleOpenNewUserModal = () => {
    if (!isOwner) return;
    setIsNewUserModalVisible(true);

    if (isNewUserSuccess) {
      resetNewUser();
    }
  };

  const renderUpdateUserSuccessMessage = () => {
    const user = usersData?.find((u) => u.id === updatedUser?.id);

    if (isRoleSuccess) {
      return `${getUserNameText(
        user?.first_name,
        user?.last_name,
      )}'s role is now ${userRoleText(updatedUser?.role)}.`;
    }

    return "Account was updated successfully.";
  };

  const renderMembersTable = () => {
    if (isUsersLoading || isUsersFetching) {
      return <LoadingContent />;
    }

    if (!usersData || usersError) {
      return null;
    }

    if (usersData.length === 0) {
      return <div className="Note">There are no team members</div>;
    }

    return (
      <MembersTable
        users={usersData}
        coSigningStatus={coSigningStatus}
        currentUserId={currentUserId}
        canManage={isOwnerViewer}
        maxCoSigners={CO_SIGNING_MAX_SIGNERS}
        onChangeRoleRequest={handleChangeRoleRequest}
        onToggleStatusRequest={handleToggleStatusRequest}
        onSimulateInvitedToPending={simulateInvitedToPending}
        onReviewCoSignerRequest={handleReviewCoSignerRequest}
        onRemoveCoSignerRequest={handleRemoveCoSignerRequest}
        onInviteCoSignerRequest={handleInviteCoSignerRequest}
        onRevokeInvitationRequest={handleRevokeInvitationRequest}
        onReactivateRequest={handleReactivateRequest}
        isPaused={isCoSigningPaused}
      />
    );
  };

  return (
    <>
      <Card>
        <div className="CardStack__card">
          <div className="CardStack__title">
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "7px",
              }}
              onClick={() => setIsAboutRolesOpen(true)}
            >
              <span>Team members</span>
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
                strokeLinejoin="round"
                name="info"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <path d="M12 8h.01"></path>
                <path d="M11 12h1v4h1"></path>
              </svg>
            </div>
            <div className="TeamMembers__titleActions">
              {isOwner ? (
                <div
                  role="button"
                  className="CardStack__dropdownMenu"
                  onClick={handleOpenNewUserModal}
                >
                  <Icon.UserPlus02 />
                </div>
              ) : null}
            </div>
          </div>

          {usersError || roleError || statusError ? (
            <Notification variant="error" title="Error" isFilled={true}>
              <ErrorWithExtras appError={usersError || roleError || statusError} />
            </Notification>
          ) : null}

          {isRoleSuccess ? (
            <NotificationWithButtons variant="success" title="" isSimpleNotif={true}>
              <span style={{ color: "var(--sds-clr-green-11)" }}>
                {renderUpdateUserSuccessMessage()}
              </span>
            </NotificationWithButtons>
          ) : null}

          {isStatusSuccess && updatedUser?.isActive === false ? (
            <div className="TeamMembers__deactivateBanner">
              <NotificationWithButtons
                variant="success"
                title={`${getUserNameText(
                  usersData?.find((u) => u.id === updatedUser?.id)?.first_name,
                  usersData?.find((u) => u.id === updatedUser?.id)?.last_name,
                )}'s account is deactivated`}
                icon={<Icon.CheckCircle />}
                onClose={() => {
                  resetStatus();
                  setUpdatedUser(null);
                }}
              >
                {updatedUser?.wasApprovedCoSigner
                  ? "They can no longer sign in to VIA Disbursement Platform. Removing them as a co-signer is a separate step that needs Owner approval."
                  : "They can no longer sign in to VIA Disbursement Platform. You can reactivate them at any time."}
              </NotificationWithButtons>
            </div>
          ) : null}
          {isStatusSuccess && updatedUser?.isActive === true ? (
            <div className="TeamMembers__deactivateBanner">
              <NotificationWithButtons
                variant="success"
                title={`${getUserNameText(
                  usersData?.find((u) => u.id === updatedUser?.id)?.first_name,
                  usersData?.find((u) => u.id === updatedUser?.id)?.last_name,
                )}'s account is reactivated`}
                icon={<Icon.CheckCircle />}
                onClose={() => {
                  resetStatus();
                  setUpdatedUser(null);
                }}
              ></NotificationWithButtons>
            </div>
          ) : null}

          {isNewUserSuccess ? (
            <NotificationWithButtons
              variant="success"
              title="New team member added"
              icon={<Icon.CheckCircle />}
              buttons={[
                {
                  label: "Dismiss",
                  onClick: () => {
                    resetNewUser();
                  },
                },
              ]}
            >
              {`Team member ${newUser.first_name} ${newUser.last_name} with role ${userRoleText(
                newUser.roles[0],
              )} was added successfully.`}
            </NotificationWithButtons>
          ) : null}

          {renderMembersTable()}
        </div>
      </Card>

      <ToggleStatusModal
        visible={isStatusModalVisible}
        user={selectedUser}
        isPending={isStatusPending}
        isApprovedCoSigner={
          selectedUser
            ? getCoSignerRowInfo(selectedUser.id, coSigningStatus)?.state === "approved"
            : false
        }
        onConfirm={handleConfirmStatus}
        onClose={hideModal}
      />

      <ChangeRoleModal
        key={selectedUser?.id ?? "no-user"}
        visible={isRoleModalVisible}
        user={selectedUser}
        isPending={isRolePending}
        onConfirm={handleConfirmRole}
        onClose={hideModal}
      />

      <CoSignerSigningModal
        key={isSigningModalVisible ? `${signingIntent}-${signingTarget?.user_id}` : "closed"}
        visible={isSigningModalVisible}
        intent={signingIntent}
        target={signingTarget}
        onClose={closeSigningModal}
        onApprove={() => {
          approvePendingSigner();
        }}
        onReject={() => {
          rejectPendingSigner();
        }}
        onRemove={() => {
          if (signingTarget) removeCoSigner(signingTarget.user_id);
        }}
      />

      <NewUserModal
        visible={isNewUserModalVisible}
        onClose={() => {
          hideModal();

          if (isNewUserError) {
            resetNewUser();
          }
        }}
        onSubmit={(nu: NewUser) => {
          if (isNewUserError) {
            resetNewUser();
          }

          const t = setTimeout(() => {
            createNewUser(nu);
            clearTimeout(t);
          }, 100);
        }}
        onResetQuery={() => {
          resetNewUser();
        }}
        isLoading={isNewUserPending}
        errorMessage={newUserError?.message}
      />

      <AboutRolesModal visible={isAboutRolesOpen} onClose={() => setIsAboutRolesOpen(false)} />
    </>
  );
};
